<template>
  <section
    v-if="
      !loading &&
      settings.accountId != null &&
      settings.placementId != null &&
      paginatedCoupons.length > 0
    "
    style="max-width: 1400px; margin: 30px auto auto; padding: 0 20px"
  >
    <v-row class="mt-3">
      <v-col
        cols="12"
        sm="6"
        md="4"
        v-for="coupon in paginatedCoupons"
        :key="coupon.index"
        role="listitem"
        aria-labelledby="coupon.title"
        class="coupon-card-container"
      >
        <article
          role="article"
          class="coupon-card"
          aria-labelledby="coupon.title"
          :id="`coupon-${coupon.code}`"
        >
          <v-img
            :src="coupon.logo"
            crossorigin="anonymous"
            alt="Card image"
            class="mb-2"
            aria-label="Coupon image"
            style="
              aspect-ratio: 16/16;
              object-fit: contain;
              background: #f5f5f5;
              width: 100%;
              height: 100%;
            "
          ></v-img>
          <h2
            class="coupon-title d-flex mt-2"
            :id="`coupon-title-${coupon.index}`"
            aria-describedby="coupon-description-{{ coupon.index }} coupon-disclaimer-{{ coupon.index }} coupon-expiration-{{ coupon.index }}"
          >
            <span
              class="color-field"
              :style="{
                fontSize: Style(coupon).fontSize,
                lineHeight: Style(coupon).lineHeight,
              }"
              >Title:
            </span>
            <div :style="Style(coupon)" style="width: 100%; margin-left: 2px">
              {{ coupon?.title }}
            </div>
          </h2>
          <div
            class="coupon-description d-flex mt-3"
            :class="{ expanded: coupon.isDescriptionExpanded }"
            :id="`coupon-description-${coupon.index}`"
          >
            <span
              class="color-field"
              :style="{
                fontSize: descriptionStyle(coupon).fontSize,
                lineHeight: descriptionStyle(coupon).lineHeight,
              }"
            >
              Description:
            </span>
            <div
              :style="descriptionStyle(coupon)"
              style="width: 100%; margin-left: 2px"
            >
              {{ coupon?.description }}
            </div>
          </div>
          <!-- <div style="border-top: 1px solid #ede6e6; " class="mt-3" ></div> -->

          <div class="disclaimer d-flex mt-3" id="coupon.disclaimer">
            <span
              class="color-field"
              :style="{
                fontSize: disclaimerStyle(coupon).fontSize,
                lineHeight: disclaimerStyle(coupon).lineHeight,
              }"
            >
              Disclaimer:
            </span>
            <div
              style="width: 100%; margin-left: 2px"
              :style="disclaimerStyle(coupon)"
            >
              {{ coupon?.disclaimer }}
            </div>
          </div>
          <div class="exp-info mt-3">
            <span class="color-field">Expiration Date: </span
            >{{ coupon.expirationInfo }}
          </div>
        </article>
        <v-btn
          aria-label="Download coupon as PDF"
          class="rounded mt-4 pa-0"
          color="black"
          outlined
          text="Download Coupon"
          @click="downloadCouponAsPdf(`coupon-${coupon.code}`)"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="totalPages > 1"
      justify="center"
      class="mt-10"
      role="navigation"
      aria-labelledby="pagination-control"
    >
      <v-select
        v-model="itemsPerPage"
        :items="[5, 10, 15, 20]"
        label="Items per page"
        class="mb-3"
        style="max-width: 150px; font-size: 14px"
        dense
        outlined
        aria-label="Select the number of items per page"
      />
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="5"
        circle
      ></v-pagination>
    </v-row>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useCounterStore } from '@/stores/counter'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { convertServerTimeToLocalTime } from '@/utils'
import { useSettingsStore } from '@/stores/settings'
import type { TransformedCoupon } from '@/interface/Coupon'

const props = defineProps<{
  accountId: string
  placementId: string
  couponId: string
}>()

const store = useCounterStore()
const settings = useSettingsStore()

const itemsPerPage = ref(5)
const currentPage = ref(1)
const loading = ref(true)

onMounted(async () => {
  try {
    settings.setAccountId(props?.accountId ?? '')
    settings.setPlacementId(props?.placementId ?? '')
    settings.setCouponId(props?.couponId ?? '')

    await store.fetchCoupons()

    loading.value = false
  } catch (error) {
    console.error('Error fetching coupons:', error)
  }
})

const displayedCoupons = computed(() => {
  return store.coupons
    .filter(coupon => coupon.status === 'Active')
    .map((coupon, index) => {
      console.log(coupon.title, coupon)
      const endTime = convertServerTimeToLocalTime(coupon.endTime)

      console.log('### END TIME ===>  ', endTime)

      return {
        index: index + 1,
        title: coupon.title,
        startTime: coupon.startTime
          ? convertServerTimeToLocalTime(coupon.startTime)
          : coupon.startTime,
        endTime,
        expirationInfo: calculateExpirationInfo(endTime),
        status: coupon.status,
        description: coupon.description || 'No description available',
        disclaimer: coupon.disclaimer || 'No disclaimer available',
        listItems: coupon.listItems || [],
        couponImage: coupon.couponImage,
        logo: coupon.logo,
        thumbnail: coupon.thumbnail,
        fontColor: coupon.fontColor || '#000000',
        descriptionfontColor: coupon?.descriptionfontColor || '#000000',
        disclaimerfontColor: coupon.disclaimerfontColor || '#000000',
        fontSize: coupon.fontSize || 16,
        descriptionfontSize: coupon?.descriptionfontSize || 16,
        disclaimerfontSize: coupon.disclaimerfontSize || 16,
        fontWeight: coupon.fontWeight || 'normal',
        descriptionfontWeight: coupon.descriptionfontWeight || 400,
        disclaimerfontWeight: coupon.disclaimerfontWeight || 400,
        textAlign: coupon.textAlign || 'left',
        descriptiontextAlign: coupon?.descriptiontextAlign || 'left',
        disclaimertextAlign: coupon.disclaimertextAlign || 'left',
        textTransform: coupon.textTransform || 'none',
        descriptiontextTransform: coupon.descriptiontextTransform || 'none',
        disclaimertextTransform: coupon.disclaimertextTransform || 'none',
        textDecoration: coupon.textDecoration || 'none',
        descriptiontextDecoration: coupon.descriptiontextDecoration || 'none',
        disclaimertextDecoration: coupon.disclaimertextDecoration || 'none',
        lineHeight: coupon.lineHeight || 1,
        descriptionlineHeight: coupon.descriptionlineHeight || 1,
        disclaimerlineHeight: coupon.disclaimerlineHeight || 1,
        height: coupon.height || 'none',
        code: coupon?.code,
        isDescriptionExpanded: false,
      }
    })
})

function calculateExpirationInfo(endStr: string) {
  if (!endStr) {
    return 'N/A'
  }
  const endDate = new Date(endStr)
  const now = new Date()
  const diffMs = endDate.getTime() - now.getTime()

  if (diffMs <= 0) {
    return 'Expired'
  }

  const msInHour = 1000 * 60 * 60
  const msInDay = msInHour * 24

  const days = Math.floor(diffMs / msInDay)
  const hours = Math.floor((diffMs % msInDay) / msInHour)
  const minutes = Math.floor((diffMs % msInHour) / (1000 * 60))

  if (days >= 1) {
    return days === 1 ? 'Tomorrow' : `${days} days left`
  }

  return `${hours} hour(s) and ${minutes} minute(s) Left`
}

// const startCountdownTimer = () => {
//   setInterval(() => {
//     displayedCoupons.value.forEach(coupon => {
//       coupon.expirationInfo = calculateExpirationInfo(coupon.endTime)
//     })
//   }, 1000)
// }

watch(currentPage, (newPage: number) => {
  localStorage.setItem('currentPage', newPage.toString())
})
watch([itemsPerPage, displayedCoupons], () => {
  if (currentPage.value > totalPages.value) {
    // either clamp...
    currentPage.value = totalPages.value || 1
    // or reset to first page:
    // currentPage.value = 1
  }
})

const Style = (coupon: TransformedCoupon) => {
  return {
    color: coupon.fontColor || '#000000',
    fontSize: `${coupon.fontSize || 16}px`,
    fontWeight: coupon.fontWeight || 'normal',
    textAlign:
      (coupon.textAlign as 'left' | 'center' | 'right' | 'justify') || 'left',
    textTransform:
      (coupon.textTransform as
        | 'none'
        | 'capitalize'
        | 'uppercase'
        | 'lowercase') || 'none',
    textDecoration:
      (coupon.textDecoration as 'none' | 'underline' | 'line-through') ||
      'none',
    lineHeight: `${coupon.lineHeight || 1}em`,
  }
}

const disclaimerStyle = (coupon: TransformedCoupon) => {
  return {
    color: coupon.disclaimerfontColor || '#000000',
    fontSize: `${coupon.disclaimerfontSize || 16}px`,
    fontWeight: coupon.disclaimerfontWeight || 400,
    textAlign:
      (coupon.disclaimertextAlign as 'left' | 'center' | 'right' | 'justify') ||
      'left',
    textTransform:
      (coupon.disclaimertextTransform as
        | 'none'
        | 'capitalize'
        | 'uppercase'
        | 'lowercase') || 'none',
    textDecoration:
      (coupon.disclaimertextDecoration as
        | 'none'
        | 'underline'
        | 'line-through') || 'none',
    lineHeight: `${coupon.disclaimerlineHeight || 1}em`,
  }
}

const descriptionStyle = (coupon: TransformedCoupon) => {
  return {
    color: coupon?.descriptionfontColor || '#000000',
    fontSize: `${coupon?.descriptionfontSize || 16}px`,
    fontWeight: coupon?.descriptionfontWeight || 400,
    textAlign:
      (coupon?.descriptiontextAlign as
        | 'left'
        | 'center'
        | 'right'
        | 'justify') || 'left',
    textTransform:
      (coupon?.descriptiontextTransform as
        | 'none'
        | 'capitalize'
        | 'uppercase'
        | 'lowercase') || 'none',
    textDecoration:
      (coupon?.descriptiontextDecoration as
        | 'none'
        | 'underline'
        | 'line-through') || 'none',
    lineHeight: `${coupon?.descriptionlineHeight || 1}em`,
  }
}

const totalPages = computed(() => {
  return Math.ceil(displayedCoupons.value.length / itemsPerPage.value)
})

const paginatedCoupons = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return displayedCoupons.value.slice(start, start + itemsPerPage.value)
})

async function toDataURL(url: string): Promise<string> {
  // debug log to see exactly what URL the browser is trying to fetch:
  console.log('⏳ fetching', url)
  const blob = await fetch(url, { mode: 'cors' }).then(r => r.blob())
  return new Promise<string>(res => {
    const fr = new FileReader()
    fr.onloadend = () => res(fr.result as string)
    fr.readAsDataURL(blob)
  })
}

async function downloadCouponAsPdf(id: string) {
  const el = document.getElementById(id)
  if (!el) throw new Error('Element not found')

  // swap in-memory data-URL for every remote img
  await Promise.all(
    [...el.querySelectorAll('img')].map(async img => {
      if (/^https?:/.test(img.src)) {
        try {
          console.log(img?.src)
          img.src = await toDataURL(img?.src)
        } catch (e) {
          console.error('Failed to inline', img.src, e)
        }
      }
    }),
  )

  const canvas = await html2canvas(el, { scale: 2 })
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [canvas.width, canvas.height],
  })
  pdf.addImage(
    canvas.toDataURL('image/png'),
    'PNG',
    0,
    0,
    canvas.width,
    canvas.height,
  )
  pdf.save(`${id}.pdf`)
}


</script>

<style scoped>
.top-bar-div {
  background-color: #f5f5f5;
  color: black !important;
  padding-left: 16px;
  padding-right: 16px;
}

.nav-btn {
  color: black !important;
  font-weight: 500;
  text-transform: none;
  margin-left: 8px;
}
.top-bar-div {
  background-color: #f5f5f5;
  color: black !important;
  padding-left: 16px;
  padding-right: 16px;
}

.nav-btn {
  color: black !important;
  font-weight: 500;
  text-transform: none;
  margin-left: 8px;
}

/* Active button styling */
.active-nav {
  font-weight: 700;
  background-color: #d3d3d3;

  color: black !important; /* primary or custom highlight */
}

.coupon-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px;
  background-color: #ffffff;
  transition: transform 0.3s ease;
  margin-bottom: 20px;
  overflow: hidden;
  flex-grow: 1;
}

.coupon-card:hover {
  transform: translateY(-5px);
}
.v-card {
  padding: 16px;
  min-height: 640px;
}
.text-center {
  text-align: center;
}

.grey--text {
  background: #f3f5f7;
  color: rgb(30, 0, 0);
  padding: 3px 5px;
  border-radius: 5px;
}
.custom-divider {
  height: 3px;
  background-color: #000;
}
.v-divider {
  height: 8px !important;
  background: #545151;
  max-height: none;
  width: 70%;
  margin-left: 65px;
  opacity: unset;
}
.v-img__img {
  width: -webkit-fill-available !important;
  height: auto !important;
}
.v-img {
  height: fit-content !important;
}

.exp-info {
  font-weight: bold;
}

.v-progress-circular {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}
.color-field {
  color: gray;
  font-weight: 600;
}

/* coupon code snippet code */
.coupon-widget-preview {
  border: 1px solid #ccc;
  padding: 1rem;
  margin-top: 2rem;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.code-block {
  position: relative;
}

.copy-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #3c3c3c;
  color: white;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.2s ease;
}

.copy-btn:hover {
  background-color: #5a5a5a;
}

.code-box {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 3.5rem 1rem 1rem 1rem;
  border-radius: 6px;
  overflow-x: auto;
  margin-top: 1rem;
  white-space: pre-wrap;
  font-family: 'Courier New', Courier, monospace;
  position: relative;
}
.code-box-inside {
  width: 90%;
}
.v-progress-circular {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}
.generate-coupon-div {
  display: flex;
  justify-content: end;
}
.coupon-card-container {
  display: flex;
  flex-direction: column;
}
</style>
